import { useState, useCallback, useMemo, useEffect } from "react";
import { IProductTexture } from "../../../entities-generated/common/product-texture";
import { StormDoorHandleType } from "../../../entities-generated/common/storm-door-handle-type";
import { IStormDoorHandleTypeOption } from "../../../entities-generated/common/storm-door-handle-type-option";
import { IStormDoorHandleEditingModel, StormDoorHandleEditingModel } from "../../../entities-generated/models/storm-door/storm-door-handle-editing-model";
import { BwjInput } from "../../../framework/entities/bwj-input";
import { BwjValidityState } from "../../../framework/entities/bwj-validity-state";
import { EventNotificationService, useEventNotification } from "../../../framework/services/event-notification";
import { ImmutableFormValueService, MutableFormValueService, ValidatedFormValueService, IFormValidityCheck } from "../../../framework/services/form-model-service-components";
import { NavigationService } from "../../../framework/services/navigation";
import { contentChanged, isNotNullOrWhitespace, isNullOrWhitespace } from "../../../framework/utils";
import { firstOrDefault, none, where } from "../../../framework/utils/collection";
import { StormHandleEvents } from "../events";
import { IHandleEditorFormModel, IHandleEditorValueModel, IHandleEditorModelChange } from "./handle-editor-iface";
import { IUpdateStormDoorHandleRequest } from "../../../entities-generated/models/storm-door/update-storm-door-handle-request";

// ##BEGIN:DEVELOPERCODE##

// this is a good place for doing things like defining custom form value service types

// ##END:DEVELOPERCODE##

// %%BEGIN:id/immutable/string%%
export const id = new (class idService extends ImmutableFormValueService<IStormDoorHandleEditingModel, string> {
    public initialize(data: IStormDoorHandleEditingModel): void {
        this.value = data.id;
    }

    public applyToDataModel(data: IStormDoorHandleEditingModel): void {
        data.id = this.value;
    }

    public getDefaultValue(): string {
        return '';
    }

    public getInputValue(): string {
        return this.value;
    }

})(
    'id' // id
    ,'' // description
);
// %%END:id/immutable/string%%

// %%BEGIN:isActive/mutable/boolean/boolean%%
export const isActive = new (class isActiveService extends MutableFormValueService<IStormDoorHandleEditingModel, boolean, boolean> {
    public initialize(data: IStormDoorHandleEditingModel): void {
        this.value = data.isActive;
    }

    public applyToDataModel(data: IStormDoorHandleEditingModel): void {
        data.isActive = this.value;
    }

    public getDefaultValue(): boolean {
        return false;
    }

    public getInputValue(): boolean {
        return this.value;
    }

    public setInputValue(value: boolean): void {
        this.value = value;
    }

    public getOutputValue(): boolean {
        return this.value;
    }

    public onUpdate(value: boolean): void {
        this.value = value;
        !!this.publishChange && this.publishChange(this.id);
    }
})(
    'isActive' // id
    ,'This handle is live' // description
);
// %%END:isActive/mutable/boolean/boolean%%

// %%BEGIN:isNotAvailable/mutable/boolean/boolean%%
export const isNotAvailable = new (class isNotAvailableService extends MutableFormValueService<IStormDoorHandleEditingModel, boolean, boolean> {
    public initialize(data: IStormDoorHandleEditingModel): void {
        this.value = data.isNotAvailable;
    }

    public applyToDataModel(data: IStormDoorHandleEditingModel): void {
        data.isNotAvailable = this.value;
    }

    public getDefaultValue(): boolean {
        return false;
    }

    public getInputValue(): boolean {
        return this.value;
    }

    public setInputValue(value: boolean): void {
        this.value = value;
    }

    public getOutputValue(): boolean {
        return this.value;
    }

    public onUpdate(value: boolean): void {
        this.value = value;
        !!this.publishChange && this.publishChange(this.id);
    }
})(
    'isNotAvailable' // id
    ,'Handle not available' // description
);
// %%END:isNotAvailable/mutable/boolean/boolean%%

// %%BEGIN:description/validated/string/string%%
export const description = new (class descriptionService extends ValidatedFormValueService<IStormDoorHandleEditingModel, string, string> {
    public initialize(data: IStormDoorHandleEditingModel): void {
        this.setInputValue(new BwjInput<string>(data.description));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IStormDoorHandleEditingModel): void {
        data.description = this.value.value;
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value.value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.requiredFieldValidator(value, 'Enter product description');
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'description' // id
    ,'Description' // description
);
// %%END:description/validated/string/string%%

// %%BEGIN:brand/validated/string/string%%
export const brand = new (class brandService extends ValidatedFormValueService<IStormDoorHandleEditingModel, string, string> {
    public initialize(data: IStormDoorHandleEditingModel): void {
        this.setInputValue(new BwjInput<string>(data.brand));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IStormDoorHandleEditingModel): void {
        data.brand = this.value.value;
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value.value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.requiredFieldValidator(value, 'Enter handle brand');
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'brand' // id
    ,'Brand' // description
);
// %%END:brand/validated/string/string%%

// %%BEGIN:handleType/validated/IStormDoorHandleTypeOption | null/StormDoorHandleType%%
export const handleType = new (class handleTypeService extends ValidatedFormValueService<IStormDoorHandleEditingModel, IStormDoorHandleTypeOption | null, StormDoorHandleType> {
    public initialize(data: IStormDoorHandleEditingModel): void {
        this.options = data.doorHandleTypes;
        const ht = firstOrDefault(this.options, h => h.id === data.handleType);
        this.setInputValue(new BwjInput<IStormDoorHandleTypeOption | null>(ht));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IStormDoorHandleEditingModel): void {
        data.doorHandleTypes = this.options;
        data.handleType = this.getOutputValue();
    }

    public getDefaultValue(): BwjInput<IStormDoorHandleTypeOption | null> {
        return new BwjInput<IStormDoorHandleTypeOption | null>(null);
    }

    public getInputValue(): BwjInput<IStormDoorHandleTypeOption | null> {
        return this.value;
    }

    public setInputValue(value: BwjInput<IStormDoorHandleTypeOption | null>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): StormDoorHandleType {
        const value = this.value.value;
        return value?.id ?? StormDoorHandleType.Indeterminate;
    }

    public validate(value: IStormDoorHandleTypeOption | null): Promise<BwjInput<IStormDoorHandleTypeOption | null>> {
        const result = this.requiredFieldValidator(value, 'Select a compatible handle type');
        return Promise.resolve(result);
    }

    public onUpdate(value: IStormDoorHandleTypeOption | null): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

    public options: IStormDoorHandleTypeOption[] = [];
})(
    'handleType' // id
    ,'Compatible handle type' // description
);
// %%END:handleType/validated/IStormDoorHandleTypeOption | null/StormDoorHandleType%%

// %%BEGIN:imageUrl/validated/string/string%%
export const imageUrl = new (class imageUrlService extends ValidatedFormValueService<IStormDoorHandleEditingModel, string, string> {
    public initialize(data: IStormDoorHandleEditingModel): void {
        this.setInputValue(new BwjInput<string>(data.imageUrl));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IStormDoorHandleEditingModel): void {
        data.imageUrl = this.value.value;
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value.value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.getInputObject(value);
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'imageUrl' // id
    ,'Primary image' // description
);
// %%END:imageUrl/validated/string/string%%

// %%BEGIN:textureVariants/mutable/IProductTexture[]/IProductTexture[]%%
export const textureVariants = new (class textureVariantsService extends MutableFormValueService<IStormDoorHandleEditingModel, IProductTexture[], IProductTexture[]> {
    public initialize(data: IStormDoorHandleEditingModel): void {
        this.value = data.textureVariants;
    }

    public applyToDataModel(data: IStormDoorHandleEditingModel): void {
        data.textureVariants = this.value;
    }

    public getDefaultValue(): IProductTexture[] {
        return [];
    }

    public getInputValue(): IProductTexture[] {
        return this.value;
    }

    public setInputValue(value: IProductTexture[]): void {
        this.value = value;
    }

    public getOutputValue(): IProductTexture[] {
        return this.value;
    }

    public onUpdate(value: IProductTexture[]): void {
        this.value = value;
        !!this.publishChange && this.publishChange(this.id);
    }
})(
    'textureVariants' // id
    ,'Material / Finish Variations' // description
);
// %%END:textureVariants/mutable/IProductTexture[]/IProductTexture[]%%

// %%BEGIN:imageUrls/validated/string/string[]%%
export const imageUrls = new (class imageUrlsService extends ValidatedFormValueService<IStormDoorHandleEditingModel, string, string[]> {
    public initialize(data: IStormDoorHandleEditingModel): void {
        this.setInputValue(new BwjInput<string>(data.auxImageUrls.join('\n')));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IStormDoorHandleEditingModel): void {
        data.auxImageUrls = this.getOutputValue();
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string[] {
        const value = this.value.value;
        const op = isNullOrWhitespace(value) ? [] : value.split('\n');
        return where(op, i => isNotNullOrWhitespace(i));
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.getInputObject(value);
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'imageUrls' // id
    ,'Additional images' // description
);
// %%END:imageUrls/validated/string/string[]%%

// %%BEGIN:sellingPoints/validated/string/string[]%%
export const sellingPoints = new (class sellingPointsService extends ValidatedFormValueService<IStormDoorHandleEditingModel, string, string[]> {
    public initialize(data: IStormDoorHandleEditingModel): void {
        this.setInputValue(new BwjInput<string>(data.sellingPoints.join('\n')));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IStormDoorHandleEditingModel): void {
        data.sellingPoints = this.getOutputValue();
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string[] {
        const value = this.value.value;
        const op = isNullOrWhitespace(value) ? [] : value.split('\n');
        return where(op, i => isNotNullOrWhitespace(i));
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.getInputObject(value);
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'sellingPoints' // id
    ,'Selling points' // description
);
// %%END:sellingPoints/validated/string/string[]%%

// %%BEGIN:price/validated/string/number%%
export const price = new (class priceService extends ValidatedFormValueService<IStormDoorHandleEditingModel, string, number> {
    public initialize(data: IStormDoorHandleEditingModel): void {
        this.setInputValue(new BwjInput<string>((data.price / 100).toFixed(2)));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IStormDoorHandleEditingModel): void {
        data.price = this.getOutputValue();
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): number {
        const v = this.value.value;
        let price = isNotNullOrWhitespace(v) ? parseFloat(v) : 0;
        price = isNaN(price) ? 0 : Math.floor(price * 100);
        return price;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.requiredFieldValidator(value, 'Enter door price');
        if(result.validity === BwjValidityState.Valid) {
            const price = parseFloat(value);
            if(isNaN(price)) {
                result.validity = BwjValidityState.Invalid;
                result.message = 'Enter a valid price';
            }
        }
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'price' // id
    ,'Price' // description
);
// %%END:price/validated/string/number%%

// %%BEGIN:inStoreQuantity/validated/string/number%%
export const inStoreQuantity = new (class inStoreQuantityService extends ValidatedFormValueService<IStormDoorHandleEditingModel, string, number> {
    public initialize(data: IStormDoorHandleEditingModel): void {
        this.setInputValue(new BwjInput<string>(data.inStoreQuantity.toString()));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IStormDoorHandleEditingModel): void {
        data.inStoreQuantity = this.getOutputValue();
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): number {
        const value = this.value.value;
        let qty = parseInt(value);
        qty = isNaN(qty) === false ? qty : 0;
        return qty;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.requiredFieldValidator(value, 'Enter the quantity in stock');
        if(result.validity === BwjValidityState.Valid) {
            const price = parseInt(value);
            if(isNaN(price)) {
                result.validity = BwjValidityState.Invalid;
                result.message = 'Enter a valid quantity';
            }
        }
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'inStoreQuantity' // id
    ,'Quantity in stock' // description
);
// %%END:inStoreQuantity/validated/string/number%%

// %%BEGIN:pickupLeadTimeInDays/validated/string/number%%
export const pickupLeadTimeInDays = new (class pickupLeadTimeInDaysService extends ValidatedFormValueService<IStormDoorHandleEditingModel, string, number> {
    public initialize(data: IStormDoorHandleEditingModel): void {
        this.setInputValue(new BwjInput<string>(data.pickupLeadTimeInDays.toString()));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IStormDoorHandleEditingModel): void {
        data.pickupLeadTimeInDays = this.getOutputValue();
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): number {
        const value = this.value.value;
        let lt = parseInt(value);
        lt = isNaN(lt) === false ? lt : 0;
        return lt;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.requiredFieldValidator(value, 'Enter pickup lead time');
        if(result.validity === BwjValidityState.Valid) {
            const lt = parseInt(value);
            if(isNaN(lt)) {
                result.validity = BwjValidityState.Invalid;
                result.message = 'Enter a valid lead time';
            }
        }
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'pickupLeadTimeInDays' // id
    ,'Pickup lead time (days)' // description
);
// %%END:pickupLeadTimeInDays/validated/string/number%%

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toFormModel = (): IHandleEditorFormModel => {
    return {
       isActive: isActive.getInputValue(),
       isNotAvailable: isNotAvailable.getInputValue(),
       description: description.getInputValue(),
       brand: brand.getInputValue(),
       handleType: handleType.getInputValue(),
       imageUrl: imageUrl.getInputValue(),
       textureVariants: textureVariants.getInputValue(),
       imageUrls: imageUrls.getInputValue(),
       sellingPoints: sellingPoints.getInputValue(),
       price: price.getInputValue(),
       inStoreQuantity: inStoreQuantity.getInputValue(),
       pickupLeadTimeInDays: pickupLeadTimeInDays.getInputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toValueModel = (): IHandleEditorValueModel => {
    return {
       id: id.getOutputValue(),
       isActive: isActive.getOutputValue(),
       isNotAvailable: isNotAvailable.getOutputValue(),
       description: description.getOutputValue(),
       brand: brand.getOutputValue(),
       handleType: handleType.getOutputValue(),
       imageUrl: imageUrl.getOutputValue(),
       textureVariants: textureVariants.getOutputValue(),
       imageUrls: imageUrls.getOutputValue(),
       sellingPoints: sellingPoints.getOutputValue(),
       price: price.getOutputValue(),
       inStoreQuantity: inStoreQuantity.getOutputValue(),
       pickupLeadTimeInDays: pickupLeadTimeInDays.getOutputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const getValidatedInputs = () => {
    return [
        description.getInputValue(),
        brand.getInputValue(),
        handleType.getInputValue(),
        imageUrl.getInputValue(),
        imageUrls.getInputValue(),
        sellingPoints.getInputValue(),
        price.getInputValue(),
        inStoreQuantity.getInputValue(),
        pickupLeadTimeInDays.getInputValue(),
    ];
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isFormValid = () => none(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const getFormErrors = () => where(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const broadcastChange = (propertyName: string) => {
    const arg: IHandleEditorModelChange = {
        propertyChanged: propertyName,
        model: toFormModel()
    };

    EventNotificationService.dispatch(StormHandleEvents.EditFormModelDidUpdate, arg);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const validateAndUpdateModel = (): Promise<IFormValidityCheck<IHandleEditorFormModel>> => {
    const promises = [
        description.formValidateAndUpdate(),
        brand.formValidateAndUpdate(),
        handleType.formValidateAndUpdate(),
        imageUrl.formValidateAndUpdate(),
        imageUrls.formValidateAndUpdate(),
        sellingPoints.formValidateAndUpdate(),
        price.formValidateAndUpdate(),
        inStoreQuantity.formValidateAndUpdate(),
        pickupLeadTimeInDays.formValidateAndUpdate(),
    ];

    return Promise.all(promises)
        .then(() => { 
            broadcastChange('validateAndUpdateModel:IHandleEditorFormModel');
            return { isValid: isFormValid(), model: toFormModel() };
        });
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isModelChanged = () => {
    if(initialState.valueModel === undefined) { return false; }
    const current = toValueModel();
    return contentChanged(initialState.valueModel, current);
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const initialState: { valueModel?: IHandleEditorValueModel, dataModel: IStormDoorHandleEditingModel } = {
    dataModel: new StormDoorHandleEditingModel()
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const initializeService = (data?: IStormDoorHandleEditingModel) => {
    const dataModel = data || new StormDoorHandleEditingModel();

    id.initialize(dataModel);
    isActive.initialize(dataModel);
    isNotAvailable.initialize(dataModel);
    description.initialize(dataModel);
    brand.initialize(dataModel);
    handleType.initialize(dataModel);
    imageUrl.initialize(dataModel);
    textureVariants.initialize(dataModel);
    imageUrls.initialize(dataModel);
    sellingPoints.initialize(dataModel);
    price.initialize(dataModel);
    inStoreQuantity.initialize(dataModel);
    pickupLeadTimeInDays.initialize(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
    broadcastChange('initializeService:IHandleEditorFormModel');
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
isActive.setChangePublisher(broadcastChange);
isNotAvailable.setChangePublisher(broadcastChange);
description.setChangePublisher(broadcastChange);
brand.setChangePublisher(broadcastChange);
handleType.setChangePublisher(broadcastChange);
imageUrl.setChangePublisher(broadcastChange);
textureVariants.setChangePublisher(broadcastChange);
imageUrls.setChangePublisher(broadcastChange);
sellingPoints.setChangePublisher(broadcastChange);
price.setChangePublisher(broadcastChange);
inStoreQuantity.setChangePublisher(broadcastChange);
pickupLeadTimeInDays.setChangePublisher(broadcastChange);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const setReversionState = () => {
    const dataModel = new StormDoorHandleEditingModel();

    id.applyToDataModel(dataModel);
    isActive.applyToDataModel(dataModel);
    isNotAvailable.applyToDataModel(dataModel);
    description.applyToDataModel(dataModel);
    brand.applyToDataModel(dataModel);
    handleType.applyToDataModel(dataModel);
    imageUrl.applyToDataModel(dataModel);
    textureVariants.applyToDataModel(dataModel);
    imageUrls.applyToDataModel(dataModel);
    sellingPoints.applyToDataModel(dataModel);
    price.applyToDataModel(dataModel);
    inStoreQuantity.applyToDataModel(dataModel);
    pickupLeadTimeInDays.applyToDataModel(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const revertChanges = () => {
    initializeService(initialState.dataModel);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModelChangeEvent = (onChangeWrappedInUseCallback: ((change: IHandleEditorModelChange) => void), subscriberId: string) =>
    useEventNotification(onChangeWrappedInUseCallback, [onChangeWrappedInUseCallback], StormHandleEvents.EditFormModelDidUpdate, subscriberId);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModel = (subscriberId: string): IHandleEditorFormModel => {
    const [getter, setter] = useState<IHandleEditorFormModel>(toFormModel());

    useFormModelChangeEvent(useCallback((change: IHandleEditorModelChange) => setter(change.model), []), subscriberId);

    return getter;
}
// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useHasUnsavedChanges = (model: IHandleEditorFormModel, warnOnUnsavedPageNavigation: boolean = false) => {
    const hasUnsavedChanges = useMemo(isModelChanged, [model]);

    useEffect(() => {
        if(warnOnUnsavedPageNavigation === false || hasUnsavedChanges === false) { return; }

        const unloadCallback = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
            return '';
        };

        window.addEventListener('beforeunload', unloadCallback);
        NavigationService.registerObjection('HandleEditorFormService');

        return () => {
            window.removeEventListener('beforeunload', unloadCallback);
            NavigationService.rescindObjection('HandleEditorFormService');
        }
    }, [warnOnUnsavedPageNavigation, hasUnsavedChanges]);

    return hasUnsavedChanges;
}
// ##BEGIN:DEVELOPERCODE##

export const toUpdateRequest = () => {
    const v = toValueModel();
    const request: IUpdateStormDoorHandleRequest = {
        id: v.id,
        isActive: v.isActive,
        description: v.description,
        brand: v.brand,
        handleType: v.handleType,
        imageUrl: v.imageUrl,
        auxImageUrls: v.imageUrls,
        sellingPoints: v.sellingPoints,
        textureVariants: v.textureVariants,
        price: v.price,
        inStoreQuantity: v.inStoreQuantity,
        isNotAvailable: v.isNotAvailable,
        pickupLeadTimeInDays: v.pickupLeadTimeInDays
    };
    return request;
}

// ##END:DEVELOPERCODE##
